import React, { useMemo } from "react";
import { Bike, ChefHat, Pizza, User } from "lucide-react";
import { OrderTypeBadge } from "../../../components/OrderTypeBadge";
import type { AguardandoEntregaItem } from "../../../services/aguardandoEntregaService";
import { BrazilDate } from "../../../utils/BrazilDate";
import { TemposEtapasBadges } from "./TemposEtapasBadges";
import { PainelPedidoOriginalButton } from "./PainelPedidoOriginalButton";
import { PainelCardActionRow } from "./PainelCardActionRow";
import {
  PainelCardAmberNote,
  PainelCardDetailRow,
  PainelCardShell,
  PainelCardTemposSection,
} from "./PainelCardShell";
import {
  contarProntosEntrega,
  dataInicioAguardandoEntregaPedido,
  nomesUnicos,
} from "../utils/agruparPedidosPainel";
import {
  resolvePainelCardUrgencia,
  resolveTempoColunaEntrega,
} from "../utils/painelCardUrgencia";
import { joinObservacoes } from "../utils/origemPainel";

export type CardPedidoEntregaFase = "aguardando_entrega" | "em_entrega";

interface CardPedidoEntregaProps {
  itens: AguardandoEntregaItem[];
  fase: CardPedidoEntregaFase;
  nowMs: number;
  onVerPedidoOriginal?: (orderId: number) => void;
}

function segundosEntre(inicioIso: string, fimMs: number): number {
  return Math.max(
    0,
    (fimMs - BrazilDate.parseAsBrazil(inicioIso).getTime()) / 1000
  );
}

function maxSegundo(
  valores: (number | null | undefined)[]
): number | null {
  const nums = valores.filter((v): v is number => v != null && v >= 0);
  return nums.length > 0 ? Math.max(...nums) : null;
}

export const CardPedidoEntrega: React.FC<CardPedidoEntregaProps> = ({
  itens,
  fase,
  nowMs,
  onVerPedidoOriginal,
}) => {
  const principal = itens[0];
  const { displayId, orderId } = principal;
  const { prontas, total, todosProntos } = contarProntosEntrega(itens);

  const customer =
    principal.pedidoIntegracao?.customerName ??
    itens.find((i) => i.pedidoIntegracao?.customerName)?.pedidoIntegracao
      ?.customerName ??
    null;

  const montadores = useMemo(
    () => nomesUnicos(itens.map((i) => i.montador?.nome)),
    [itens]
  );

  const entregador =
    itens.find((i) => i.entregador?.nome)?.entregador?.nome ?? null;

  const observation = joinObservacoes(
    ...itens.flatMap((i) => [
      i.observacao,
      i.pedidoIntegracao?.observation,
      i.pedidoIntegracao?.internalObservation,
    ])
  );

  const aguardandoEntregaSeg =
    fase === "aguardando_entrega" && todosProntos
      ? (() => {
          const inicio = dataInicioAguardandoEntregaPedido(itens);
          if (!inicio) return null;
          const concluido = itens.find((i) => i.dataFim != null);
          if (concluido?.dataFim) {
            return Math.round(
              (BrazilDate.parseAsBrazil(concluido.dataFim).getTime() -
                BrazilDate.parseAsBrazil(inicio).getTime()) /
                1000
            );
          }
          return Math.round(segundosEntre(inicio, nowMs));
        })()
      : null;

  const emEntregaSeg = maxSegundo(
    itens.map((item) =>
      item.entregaDataInicio != null
        ? Math.round(
            segundosEntre(item.entregaDataInicio ?? item.dataInicio, nowMs)
          )
        : null
    )
  );

  const tempoColunaSeg = resolveTempoColunaEntrega(itens, fase, nowMs);
  const urgencia = resolvePainelCardUrgencia(tempoColunaSeg);

  const headerEnd = (
    <OrderTypeBadge
      orderType={principal.pedidoIntegracao?.orderType}
      iconSize={14}
      withTooltip={false}
    />
  );

  return (
    <PainelCardShell
      displayId={displayId}
      urgencia={urgencia}
      headerEnd={headerEnd}
      className={todosProntos ? "" : "opacity-75"}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ${
            todosProntos
              ? "bg-emerald-100 text-emerald-900"
              : "bg-amber-100 text-amber-900"
          }`}
        >
          <Pizza size={14} />
          {total === 1
            ? "1 pizza"
            : todosProntos
              ? `${total} pizzas`
              : `${prontas}/${total} prontas`}
        </span>
      </div>

      <div>
        {customer && (
          <PainelCardDetailRow icon={User}>{customer}</PainelCardDetailRow>
        )}
        {montadores.length > 0 && (
          <PainelCardDetailRow icon={ChefHat}>
            {montadores.length === 1
              ? montadores[0]
              : `${montadores.length} montadores`}
          </PainelCardDetailRow>
        )}
        {entregador && (
          <PainelCardDetailRow icon={Bike}>{entregador}</PainelCardDetailRow>
        )}
      </div>

      {observation && <PainelCardAmberNote>{observation}</PainelCardAmberNote>}

      <PainelCardTemposSection>
        <TemposEtapasBadges
          displayId={displayId}
          aguardandoEntregaSeg={
            fase === "aguardando_entrega" ? aguardandoEntregaSeg : null
          }
          emEntregaSeg={fase === "em_entrega" ? emEntregaSeg : null}
        />
      </PainelCardTemposSection>

      {onVerPedidoOriginal && (
        <PainelCardActionRow>
          <PainelPedidoOriginalButton
            orderId={orderId}
            onOpen={onVerPedidoOriginal}
          />
        </PainelCardActionRow>
      )}
    </PainelCardShell>
  );
};
